import { World } from '../ecs/World';
import { TransformComponent } from '../ecs/components/TransformComponent';
import { SpriteComponent } from '../ecs/components/SpriteComponent';
import { MovementComponent } from '../ecs/components/MovementComponent';
import { HealthComponent } from '../ecs/components/HealthComponent';
import {
  BeastieComponent,
  ElementType,
  PersonalityType,
  BeastieStats,
} from '../ecs/components/BeastieComponent';
import { AIComponent } from '../ecs/components/AIComponent';
import { CombatComponent } from '../ecs/components/CombatComponent';
import { TamingComponent } from '../ecs/components/TamingComponent';
import { SpawnPoint } from '../world/TilemapManager';
import { EventBus } from '../utils/EventBus';

/**
 * Configuration for a wild beastie species
 */
export interface WildBeastieConfig {
  speciesId: string;
  level: number;
  elementTypes: ElementType[];
  textureKey: string;
  baseStats: BeastieStats;
}

/**
 * Spawner configuration
 */
export interface SpawnConfig {
  maxWildBeasties: number;
  respawnDelayMs: number;
  despawnRangeFromPlayer: number;
}

/**
 * WildBeastieSpawner - Manages spawning and despawning of wild beasties
 * Uses fixed spawn points from tilemap
 */
export class WildBeastieSpawner {
  private world: World;
  private spawnPoints: SpawnPoint[] = [];
  private activeWildBeasties: Map<string, string> = new Map(); // spawnPointName -> entityId
  private spawnCooldowns: Map<string, number> = new Map();
  private config: SpawnConfig;
  private nextEntityId = 0;

  // Species pool for random spawning
  private speciesPool: WildBeastieConfig[] = [
    {
      speciesId: 'verdling',
      level: 3,
      elementTypes: ['verdant'],
      textureKey: 'beastie_placeholder', // Use placeholder until real sprites exist
      baseStats: { health: 40, attack: 35, defense: 45, speed: 50, energy: 40 },
    },
    {
      speciesId: 'aquafin',
      level: 4,
      elementTypes: ['aqua'],
      textureKey: 'beastie_placeholder',
      baseStats: { health: 45, attack: 40, defense: 35, speed: 60, energy: 50 },
    },
    {
      speciesId: 'terrapod',
      level: 3,
      elementTypes: ['terra'],
      textureKey: 'beastie_placeholder',
      baseStats: { health: 55, attack: 45, defense: 55, speed: 30, energy: 35 },
    },
  ];

  constructor(world: World, config?: Partial<SpawnConfig>) {
    this.world = world;
    this.config = {
      maxWildBeasties: 10,
      respawnDelayMs: 30000, // 30 seconds
      despawnRangeFromPlayer: 50,
      ...config,
    };
  }

  /**
   * Initialize with spawn points from tilemap
   */
  public setSpawnPoints(spawnPoints: SpawnPoint[]): void {
    // Filter for wild beastie spawn points
    this.spawnPoints = spawnPoints.filter(
      (sp) =>
        sp.type === 'wild_spawn' ||
        sp.type === 'beastie_spawn' ||
        sp.name.toLowerCase().includes('wild')
    );
    console.log(`WildBeastieSpawner initialized with ${this.spawnPoints.length} spawn points`);
  }

  /**
   * Add hardcoded spawn points (for testing without Tiled map)
   */
  public addTestSpawnPoints(centerX: number, centerY: number): void {
    const testPoints: SpawnPoint[] = [
      {
        name: 'wild_spawn_1',
        type: 'wild_spawn',
        x: centerX + 16,
        y: centerY + 12,
        properties: { minLevel: 2, maxLevel: 5 },
      },
      {
        name: 'wild_spawn_2',
        type: 'wild_spawn',
        x: centerX,
        y: centerY + 4,
        properties: { minLevel: 3, maxLevel: 6 },
      },
      {
        name: 'wild_spawn_3',
        type: 'wild_spawn',
        x: centerX + 3,
        y: centerY,
        properties: { minLevel: 2, maxLevel: 4 },
      },
    ];

    this.spawnPoints.push(...testPoints);
    console.log(`Added ${testPoints.length} test spawn points`);
  }

  /**
   * Update spawner - call each frame
   */
  public update(_delta: number): void {
    const currentTime = Date.now();

    // Check each spawn point
    for (const spawnPoint of this.spawnPoints) {
      // Skip if already has active beastie
      if (this.activeWildBeasties.has(spawnPoint.name)) {
        // Check if beastie still exists
        const entityId = this.activeWildBeasties.get(spawnPoint.name)!;
        const entity = this.world.getEntity(entityId);
        if (!entity) {
          // Beastie was removed, set cooldown for respawn
          this.activeWildBeasties.delete(spawnPoint.name);
          this.spawnCooldowns.set(spawnPoint.name, currentTime + this.config.respawnDelayMs);
        }
        continue;
      }

      // Check cooldown
      const cooldownEnd = this.spawnCooldowns.get(spawnPoint.name) || 0;
      if (currentTime < cooldownEnd) continue;

      // Check max limit
      if (this.activeWildBeasties.size >= this.config.maxWildBeasties) continue;

      // Spawn new wild beastie
      this.spawnWildBeastie(spawnPoint);
    }

    // Check for despawning distant beasties
    this.checkDespawn();
  }

  /**
   * Spawn a wild beastie at a spawn point
   */
  private spawnWildBeastie(spawnPoint: SpawnPoint): void {
    // Select random species (or use spawn point properties)
    const speciesConfig = this.getSpeciesForSpawnPoint(spawnPoint);

    // Determine level (use spawn point property or random range)
    const level = this.getLevelForSpawnPoint(spawnPoint, speciesConfig.level);

    const entityId = `wild_beastie_${this.nextEntityId++}`;
    const entity = this.world.createEntity(entityId);

    entity.addTag('beastie');
    entity.addTag('wild');
    entity.addTag('enemy'); // Wild beasties are initially hostile

    entity.addComponent(
      new TransformComponent({
        x: spawnPoint.x,
        y: spawnPoint.y,
        z: 0,
        facing: Math.floor(Math.random() * 8), // Random facing
      })
    );

    entity.addComponent(
      new MovementComponent({
        maxSpeed: 2.5 + Math.random() * 1.5,
        acceleration: 12,
        friction: 12,
      })
    );

    entity.addComponent(
      new SpriteComponent({
        textureKey: speciesConfig.textureKey,
        currentAnimation: 'idle',
      })
    );

    // Scale HP with level
    const maxHP = Math.floor(speciesConfig.baseStats.health * (1 + level * 0.1));
    entity.addComponent(
      new HealthComponent({
        max: maxHP,
        current: maxHP,
        maxEnergy: speciesConfig.baseStats.energy,
        energy: speciesConfig.baseStats.energy,
      })
    );

    entity.addComponent(
      new BeastieComponent({
        speciesId: speciesConfig.speciesId,
        nickname: '', // Wild beasties have no nickname
        level: level,
        elementTypes: speciesConfig.elementTypes,
        loyalty: 0, // Wild = 0 loyalty
        personality: this.randomPersonality(),
        isWild: true,
        ownerId: '',
        baseStats: speciesConfig.baseStats,
      })
    );

    // Generate patrol path around spawn point
    const patrolPath = this.generatePatrolPath(spawnPoint);

    entity.addComponent(
      new AIComponent({
        state: 'patrol',
        targetingPriority: 'nearest',
        leashRange: 5,
        detectionRange: 4,
        isActive: true,
        homePosition: { x: spawnPoint.x, y: spawnPoint.y },
        patrolPath: patrolPath,
      })
    );

    entity.addComponent(
      new CombatComponent({
        attackSpeed: 0.8,
        attackRange: 1.2,
        baseDamage: Math.floor(speciesConfig.baseStats.attack / 5),
        canInitiateCombat: true,
        isHostile: true,
      })
    );

    // Add taming component for capture mechanic
    entity.addComponent(
      new TamingComponent({
        captureThreshold: 50 + level * 2,
        feedCooldownMs: 2000,
        loyaltyDecayRate: 0.5,
      })
    );

    this.activeWildBeasties.set(spawnPoint.name, entityId);

    EventBus.emit('beastie:spawned', {
      id: entityId,
      speciesId: speciesConfig.speciesId,
      position: { x: spawnPoint.x, y: spawnPoint.y },
    });

    console.log(
      `Spawned wild ${speciesConfig.speciesId} Lv.${level} at (${spawnPoint.x.toFixed(1)}, ${spawnPoint.y.toFixed(1)})`
    );
  }

  private getSpeciesForSpawnPoint(spawnPoint: SpawnPoint): WildBeastieConfig {
    // Check if spawn point specifies a species
    if (spawnPoint.properties.species) {
      const specified = this.speciesPool.find(
        (s) => s.speciesId === spawnPoint.properties.species
      );
      if (specified) return specified;
    }

    // Random from pool
    return this.speciesPool[Math.floor(Math.random() * this.speciesPool.length)];
  }

  private getLevelForSpawnPoint(spawnPoint: SpawnPoint, defaultLevel: number): number {
    const minLevel = (spawnPoint.properties.minLevel as number) || defaultLevel;
    const maxLevel = (spawnPoint.properties.maxLevel as number) || minLevel + 2;
    return Math.floor(minLevel + Math.random() * (maxLevel - minLevel + 1));
  }

  private generatePatrolPath(spawnPoint: SpawnPoint): { x: number; y: number }[] {
    // Simple patrol around home position
    const range = 1.5;
    return [
      { x: spawnPoint.x + range, y: spawnPoint.y },
      { x: spawnPoint.x + range, y: spawnPoint.y + range },
      { x: spawnPoint.x, y: spawnPoint.y + range },
      { x: spawnPoint.x - range, y: spawnPoint.y },
      { x: spawnPoint.x, y: spawnPoint.y - range },
    ];
  }

  private randomPersonality(): PersonalityType {
    const personalities: PersonalityType[] = ['aggressive', 'defensive', 'cautious', 'bold'];
    return personalities[Math.floor(Math.random() * personalities.length)];
  }

  private checkDespawn(): void {
    // Get player position for distance check
    const player = this.world.getEntity('player');
    if (!player) return;

    const playerTransform = player.getComponent<TransformComponent>('transform');
    if (!playerTransform) return;

    for (const [spawnName, entityId] of this.activeWildBeasties) {
      const entity = this.world.getEntity(entityId);
      if (!entity) continue;

      const transform = entity.getComponent<TransformComponent>('transform');
      if (!transform) continue;

      const distance = Math.sqrt(
        Math.pow(transform.x - playerTransform.x, 2) + Math.pow(transform.y - playerTransform.y, 2)
      );

      if (distance > this.config.despawnRangeFromPlayer) {
        this.world.destroyEntity(entityId);
        this.activeWildBeasties.delete(spawnName);
        console.log(`Despawned ${entityId} - too far from player`);
      }
    }
  }

  /**
   * Remove a beastie from active tracking (e.g., when captured)
   */
  public removeFromTracking(entityId: string): void {
    for (const [spawnName, id] of this.activeWildBeasties) {
      if (id === entityId) {
        this.activeWildBeasties.delete(spawnName);
        break;
      }
    }
  }

  /**
   * Get all active wild beastie IDs
   */
  public getActiveWildBeasties(): string[] {
    return Array.from(this.activeWildBeasties.values());
  }

  public destroy(): void {
    this.activeWildBeasties.clear();
    this.spawnCooldowns.clear();
  }
}
